/**
 * HumanMouseTemplate - 通用真人鼠标效果模板
 * 
 * 功能：
 * - 模拟真实人类鼠标移动轨迹（使用 ghost-cursor）
 * - 支持 move（移动）、click（点击）、type（打字）三种动作
 * - Spring 动画平滑过渡
 * - 可选鼠标轨迹显示
 * 
 * 使用示例：
 * ```tsx
 * <Composition
 *   id="MyMouseVideo"
 *   component={HumanMouseTemplate}
 *   durationInFrames={300}
 *   fps={30}
 *   width={1920}
 *   height={1080}
 *   defaultProps={{
 *     background: 'baidu-screenshot.png',
 *     waypoints: [
 *       { x: 200, y: 200, action: 'move' },
 *       { x: 800, y: 400, action: 'click' },
 *       { x: 660, y: 395, action: 'type', text: '搜索内容' },
 *       { x: 1000, y: 450, action: 'click' }
 *     ],
 *     mouseSpeed: 5,
 *     showTrail: false
 *   }}
 * />
 * ```
 */

import React, { useMemo } from 'react';
import {
  useCurrentFrame,
  useVideoConfig,
  AbsoluteFill,
  interpolate,
  spring,
  Img,
  staticFile,
} from 'remotion';

// 条件导入 ghost-cursor，在构建环境失败时使用 fallback
type Vector = { x: number; y: number };
type PathFunction = (start: Vector, end: Vector) => Vector[];

let ghostPath: PathFunction | null = null;

// 在运行时尝试加载 ghost-cursor
// 使用 webpackIgnore 注释避免构建时警告
try {
  if (typeof window !== 'undefined' || typeof process !== 'undefined') {
    ghostPath = require(/* webpackIgnore: true */ 'ghost-cursor').path;
  }
} catch (e) {
  // ghost-cursor 不可用，使用 fallback
}

// Fallback: 简单的线性插值路径生成
const fallbackPath = (start: Vector, end: Vector): Vector[] => {
  const result: Vector[] = [];
  const steps = 60;
  for (let i = 0; i <= steps; i++) {
    result.push({
      x: start.x + (end.x - start.x) * (i / steps),
      y: start.y + (end.y - start.y) * (i / steps),
    });
  }
  return result;
};

// 统一的路径生成函数
const generatePath = (start: Vector, end: Vector): Vector[] => {
  if (ghostPath) {
    try {
      return ghostPath(start, end);
    } catch (e) {
      console.warn('ghost-cursor path generation failed, using fallback');
      return fallbackPath(start, end);
    }
  }
  return fallbackPath(start, end);
};

// ============================================================================
// 类型定义
// ============================================================================

/**
 * 鼠标动作类型
 * - move: 仅移动，无其他动作
 * - click: 点击（会有点击动画）
 * - type: 打字（会显示文字输入动画）
 */
export type MouseAction = 'move' | 'click' | 'type';

/**
 * 路径关键点
 */
export interface Waypoint {
  x: number;
  y: number;
  action: MouseAction;
  text?: string; // 仅 action='type' 时使用
}

/**
 * 组件属性
 */
export type HumanMouseTemplateProps = Record<string, unknown> & {
  // 背景图片（放在 public 文件夹）
  background: string;
  
  // 鼠标路径关键点
  waypoints: Waypoint[];
  
  // 鼠标移动速度 (1-10)，越大越快，默认 5
  mouseSpeed?: number;
  
  // 是否显示鼠标轨迹，默认 false
  showTrail?: boolean;
  
  // 起始位置（可选，默认为第一个 waypoint）
  startPosition?: { x: number; y: number };
};

// ============================================================================
// 工具函数
// ============================================================================

/**
 * 计算两点之间的距离
 */
const distance = (p1: { x: number; y: number }, p2: { x: number; y: number }): number => {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
};

/**
 * 根据距离和速度计算移动所需帧数
 * @param dist 距离（像素）
 * @param speed 速度 (1-10)
 * @param fps 帧率
 * @returns 帧数
 */
const calculateMoveFrames = (dist: number, speed: number, fps: number): number => {
  // 速度映射：1 -> 慢 (120px/s), 10 -> 快 (600px/s)
  const pixelsPerSecond = 80 + speed * 60;
  const seconds = dist / pixelsPerSecond;
  return Math.max(Math.round(seconds * fps), 15); // 最少 15 帧
};

/**
 * 计算打字所需帧数
 * @param text 要打出的文字
 * @param fps 帧率
 * @returns 帧数
 */
const calculateTypeFrames = (text: string, fps: number): number => {
  // 每个字符约 5-8 帧（模拟真实打字速度）
  const framesPerChar = 6;
  return text.length * framesPerChar + 10; // 额外 10 帧缓冲
};

/**
 * 计算点击所需帧数
 */
const CLICK_FRAMES = 12;

/**
 * 计算每个阶段的起始和结束帧
 */
interface Segment {
  type: 'move' | 'click' | 'type';
  startFrame: number;
  endFrame: number;
  fromPos: { x: number; y: number };
  toPos: { x: number; y: number };
  text?: string;
  path?: Array<{ x: number; y: number }>;
}

const calculateSegments = (
  waypoints: Waypoint[],
  mouseSpeed: number,
  fps: number,
  startPosition?: { x: number; y: number }
): Segment[] => {
  const segments: Segment[] = [];
  let currentFrame = 0;
  let currentPos = startPosition || { x: waypoints[0]?.x || 0, y: waypoints[0]?.y || 0 };

  for (let i = 0; i < waypoints.length; i++) {
    const wp = waypoints[i];
    const isFirstMove = i === 0 && !startPosition;
    
    // 移动阶段
    const dist = distance(currentPos, { x: wp.x, y: wp.y });
    let moveFrames = isFirstMove ? 0 : calculateMoveFrames(dist, mouseSpeed, fps);
    
    if (moveFrames > 0) {
      segments.push({
        type: 'move',
        startFrame: currentFrame,
        endFrame: currentFrame + moveFrames,
        fromPos: currentPos,
        toPos: { x: wp.x, y: wp.y },
      });
      currentFrame += moveFrames;
    }
    
    // 动作阶段
    if (wp.action === 'click') {
      segments.push({
        type: 'click',
        startFrame: currentFrame,
        endFrame: currentFrame + CLICK_FRAMES,
        fromPos: { x: wp.x, y: wp.y },
        toPos: { x: wp.x, y: wp.y },
      });
      currentFrame += CLICK_FRAMES;
    } else if (wp.action === 'type' && wp.text) {
      const typeFrames = calculateTypeFrames(wp.text, fps);
      segments.push({
        type: 'type',
        startFrame: currentFrame,
        endFrame: currentFrame + typeFrames,
        fromPos: { x: wp.x, y: wp.y },
        toPos: { x: wp.x, y: wp.y },
        text: wp.text,
      });
      currentFrame += typeFrames;
    }
    
    currentPos = { x: wp.x, y: wp.y };
  }

  return segments;
};

// ============================================================================
// 子组件
// ============================================================================

/**
 * 鼠标光标 SVG
 */
const MouseCursor: React.FC<{
  x: number;
  y: number;
  clicking?: boolean;
}> = ({ x, y, clicking = false }) => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      style={{
        position: 'absolute',
        left: x,
        top: y,
        transform: clicking
          ? 'translate(-2px, -2px) scale(0.9)'
          : 'translate(-2px, -2px)',
        pointerEvents: 'none',
        filter: clicking
          ? 'drop-shadow(0 0 8px rgba(0, 0, 0, 0.5))'
          : 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))',
        transition: clicking ? 'transform 0.05s' : 'none',
      }}
    >
      <path
        d="M5.5 2l10.5 10.5-4.5 1.5 2.5 6.5-2 1-2.5-6.5-3.5 3.5z"
        fill={clicking ? '#333' : '#fff'}
        stroke="#000"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/**
 * 打字文字动画
 */
const TypingText: React.FC<{
  text: string;
  progress: number;
  x: number;
  y: number;
  visible: boolean;
}> = ({ text, progress, x, y, visible }) => {
  if (!visible) return null;

  const displayText = text.slice(0, Math.floor(progress));
  const cursorOpacity = Math.sin(progress * 8) > 0 ? 1 : 0.3;

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        fontFamily: 'Arial, sans-serif',
        fontSize: 18,
        color: '#222',
        fontWeight: 400,
        whiteSpace: 'nowrap',
        transform: 'translateY(-50%)',
      }}
    >
      {displayText}
      <span
        style={{
          opacity: cursorOpacity,
          borderLeft: '2px solid #4e6ef2',
          paddingLeft: '2px',
          marginLeft: displayText.length > 0 ? '2px' : '0',
        }}
      >
        &nbsp;
      </span>
    </div>
  );
};

/**
 * 鼠标轨迹
 */
const MouseTrail: React.FC<{
  positions: Array<{ x: number; y: number }>;
}> = ({ positions }) => {
  if (positions.length < 2) return null;

  const pathD = positions
    .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
    .join(' ');

  return (
    <svg
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      <path
        d={pathD}
        fill="none"
        stroke="rgba(78, 110, 242, 0.4)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="5,5"
      />
    </svg>
  );
};

// ============================================================================
// 主组件
// ============================================================================

export const HumanMouseTemplate = ({
  background,
  waypoints,
  mouseSpeed = 5,
  showTrail = false,
  startPosition,
}: HumanMouseTemplateProps) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 计算所有动作片段
  const segments = useMemo(
    () => calculateSegments(waypoints, mouseSpeed, fps, startPosition),
    [waypoints, mouseSpeed, fps, startPosition]
  );

  // 生成每段路径（使用条件导入的路径生成函数）
  const paths = useMemo(() => {
    const result: Map<number, Array<{ x: number; y: number }>> = new Map();
    
    segments.forEach((seg, index) => {
      if (seg.type === 'move') {
        const route = generatePath(seg.fromPos, seg.toPos);
        result.set(index, route);
      }
    });
    
    return result;
  }, [segments]);

  // 轨迹历史记录
  const trailHistory = useMemo(() => {
    const history: Array<{ x: number; y: number }> = [];
    return history;
  }, []);

  // 找到当前帧所在的片段
  const currentSegment = segments.find(
    (seg) => frame >= seg.startFrame && frame < seg.endFrame
  );
  const lastSegment = segments[segments.length - 1];

  // 计算当前鼠标位置和状态
  let mousePosition = startPosition || { x: waypoints[0]?.x || 0, y: waypoints[0]?.y || 0 };
  let isClicking = false;
  let textProgress = 0;
  let showText = false;
  let currentText = '';
  let textPosition = { x: 0, y: 0 };

  if (currentSegment) {
    const localFrame = frame - currentSegment.startFrame;
    const segmentDuration = currentSegment.endFrame - currentSegment.startFrame;

    if (currentSegment.type === 'move') {
      // 移动阶段：使用 spring 动画
      const progress = spring({
        frame: localFrame,
        fps,
        config: {
          damping: 20,
          stiffness: 100,
          mass: 0.5,
        },
      });

      const pathData = paths.get(segments.indexOf(currentSegment));
      if (pathData && pathData.length > 0) {
        const pathIndex = Math.min(
          Math.floor(progress * (pathData.length - 1)),
          pathData.length - 1
        );
        if (pathData[pathIndex]) {
          mousePosition = pathData[pathIndex];
        }
      } else {
        // 简单插值
        mousePosition = {
          x: interpolate(progress, [0, 1], [currentSegment.fromPos.x, currentSegment.toPos.x]),
          y: interpolate(progress, [0, 1], [currentSegment.fromPos.y, currentSegment.toPos.y]),
        };
      }
    } else if (currentSegment.type === 'click') {
      // 点击阶段
      mousePosition = currentSegment.toPos;
      isClicking = localFrame < 6; // 点击持续 6 帧
    } else if (currentSegment.type === 'type' && currentSegment.text) {
      // 打字阶段
      mousePosition = currentSegment.toPos;
      showText = true;
      currentText = currentSegment.text;
      textPosition = { x: currentSegment.toPos.x - 140, y: currentSegment.toPos.y };
      textProgress = interpolate(
        localFrame,
        [0, segmentDuration],
        [0, currentSegment.text.length],
        { extrapolateRight: 'clamp' }
      );
    }
  } else if (frame >= (lastSegment?.endFrame || 0)) {
    // 超出最后片段，保持在最后位置
    mousePosition = lastSegment?.toPos || mousePosition;
    if (lastSegment?.type === 'type' && lastSegment.text) {
      showText = true;
      currentText = lastSegment.text;
      textPosition = { x: lastSegment.toPos.x - 140, y: lastSegment.toPos.y };
      textProgress = currentText.length;
    }
  }

  // 更新轨迹历史
  if (showTrail) {
    trailHistory.push({ ...mousePosition });
    // 限制轨迹长度
    if (trailHistory.length > 100) {
      trailHistory.shift();
    }
  }

  return (
    <AbsoluteFill style={{ backgroundColor: '#fff' }}>
      {/* 背景图片 */}
      <Img
        src={staticFile(background)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />

      {/* 鼠标轨迹 */}
      {showTrail && <MouseTrail positions={trailHistory} />}

      {/* 打字文字 */}
      <TypingText
        text={currentText}
        progress={textProgress}
        x={textPosition.x}
        y={textPosition.y}
        visible={showText}
      />

      {/* 鼠标光标 */}
      <MouseCursor
        x={mousePosition.x}
        y={mousePosition.y}
        clicking={isClicking}
      />
    </AbsoluteFill>
  );
};

// ============================================================================
// 计算总时长工具函数
// ============================================================================

/**
 * 根据 waypoints 计算视频总帧数
 * 可用于动态设置 Composition 的 durationInFrames
 */
export const calculateTotalFrames = (
  waypoints: Waypoint[],
  mouseSpeed: number = 5,
  fps: number = 30,
  startPosition?: { x: number; y: number }
): number => {
  const segments = calculateSegments(waypoints, mouseSpeed, fps, startPosition);
  return segments.length > 0
    ? segments[segments.length - 1].endFrame
    : 0;
};
